import { faces, rollTotal } from "./faces";
import type { LiveDiceRollFeedRoll } from "../types/generated/live";
import type { Roll } from "./pool";

export type Verdict = "success" | "failure" | "critical_hit" | "critical_miss";

export type RollData = {
	name: string;
	notation: string;
	result: string;
	description: string | null;
	// A line under the roll, such as that only its roller sees it; a feed entry carries none.
	note?: string | null;
	// The mark beside the note: the eye of a roll only its roller sees (the default), or the masks of one posted under "?????".
	noteIcon?: "masks";
	verdict: Verdict | null;
	color: string | null;
};

// Tells the rolls a feed has just received from the ones it already showed.
// A (re)load only primes the watch: what it brings is history, not news.
export function createRollWatch() {
	const seen = new Set<string>();
	return {
		prime(ids: string[]) {
			for (const id of ids) seen.add(id);
		},
		fresh(ids: string[]) {
			const found = ids.filter((id) => !seen.has(id));
			for (const id of found) seen.add(id);
			return found;
		},
	};
}

// A roll as the feed entry carries it (_dice_roll.html.erb); null for a
// censored entry, which carries nothing.
export function rollData(element: HTMLElement): RollData | null {
	const { rollName, rollNotation, rollResult, rollDescription, rollVerdict, rollColor } = element.dataset;
	if (!rollNotation) return null;
	return {
		name: rollName ?? "",
		notation: rollNotation,
		result: rollResult ?? "",
		description: rollDescription || null,
		verdict: (rollVerdict || null) as Verdict | null,
		color: rollColor || null,
	};
}

// A roll as the rolls feed client holds it.
export function feedRollData(roll: LiveDiceRollFeedRoll): RollData {
	return {
		name: roll.name,
		notation: roll.notation,
		result: roll.result,
		description: roll.description || null,
		verdict: (roll.verdict || null) as Verdict | null,
		color: roll.color,
	};
}

// Whether a roll is the viewer's: the feed names an account's roll by its
// @handle. With no handle (a visitor, the table view) no roll is.
export function isOwnRoll({ name }: Pick<RollData, "name">, handle: string | null | undefined) {
	return Boolean(handle) && name === `@${handle}`;
}

export function rollVerdict({ success, critical }: Pick<Roll, "success" | "critical">): Verdict | null {
	if (success !== undefined) return success ? "success" : "failure";
	return critical ? `critical_${critical}` : null;
}

// The feed's hidden result sentence, with the roller's name: each die, then the modifier.
export function rollSummary(template: string, { name, notation, result }: Pick<RollData, "name" | "notation" | "result">) {
	const pair = faces(notation, result);
	const dice = pair
		? [...pair.dice.map(([sides, value]) => `d${sides}: ${value}`), ...(pair.modifier ? [`${pair.modifier > 0 ? "+" : ""}${pair.modifier}`] : [])].join(", ")
		: result;
	const values: Record<string, string> = { name, notation, dice, total: String(rollTotal(result)) };
	return template.replace(/%\{(\w+)\}/g, (match, key: string) => values[key] ?? match);
}
